import { IDictionary } from 'models/dictionary';

const DB_NAME = 'fc-system-dictionaries';
const DB_VERSION = 1;
const STORE_NAME = 'dictionaries';

export interface ICachedDictionary {
  key: string;
  type: string;
  lang: string;
  items: IDictionary[];
  hash: string;
}

export const buildDictionaryCacheKey = (type: string, lang: string): string =>
  `${type}::${lang}`;

const isIndexedDbAvailable = (): boolean =>
  typeof window !== 'undefined' && !!window.indexedDB;

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const getCachedDictionary = async (
  key: string,
): Promise<ICachedDictionary | undefined> => {
  const db = await openDb();
  try {
    return await new Promise<ICachedDictionary | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result as ICachedDictionary | undefined);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
};

export const getAllCachedDictionaries = async (): Promise<ICachedDictionary[]> => {
  const db = await openDb();
  try {
    return await new Promise<ICachedDictionary[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve(request.result as ICachedDictionary[]);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
};

export const setCachedDictionary = async (record: ICachedDictionary): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const deleteCachedDictionary = async (key: string): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const clearCachedDictionaries = async (): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};
